/**
 * El carrusel de patrocinadores.
 *
 * CÓMO SE MUEVE, Y POR QUÉ ASÍ: es una tira con scroll horizontal nativo y
 * `scroll-snap`. En el celular se arrastra con el dedo y el navegador hace
 * todo —inercia, rebote, snap— sin una línea de JavaScript. En computadora,
 * donde no hay dedo, se agregan dos botones que empujan la tira.
 *
 * Lo que NO hace, también a propósito:
 *
 *  · NO gira solo. Un carrusel que se mueve mientras alguien lee le cambia lo
 *    que está mirando; y quien tiene un patrocinador ahí quiere que se vea, no
 *    que pase. Si alguna vez gira, tendrá que pararse al pasar el mouse y
 *    respetar `prefers-reduced-motion`.
 *  · NO es un widget con `role="tablist"` ni con teclas propias. Es una lista
 *    de enlaces dentro de algo que se puede desplazar: el lector de pantalla
 *    ya sabe leerla y el teclado ya sabe recorrerla con Tab. Reinventar eso es
 *    cómo se rompe la accesibilidad creyendo que se está agregando.
 *
 * El contenedor lleva `tabindex={0}` porque un área desplazable tiene que
 * poder recorrerse con el teclado: sin eso, quien no usa mouse no puede llegar
 * al contenido que quedó fuera de la vista.
 */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Sponsor } from '@tennisup/contracts';

import { assetUrl } from '../lib/api';

export function SponsorCarousel({
  sponsors,
}: {
  sponsors: readonly Sponsor[] | undefined;
}): JSX.Element | null {
  const { t } = useTranslation();
  const track = useRef<HTMLUListElement>(null);

  /**
   * AUSENTE ES LO MISMO QUE VACÍA, y no un error.
   *
   * El tipo dice `Sponsor[]`, pero un tipo es una promesa sobre el CONTRATO y
   * no una garantía sobre lo que viaja por la red: contra un servidor que
   * todavía no manda el campo —el de Javier hasta que reinicie, el de
   * cualquiera durante un despliegue a medias— llega `undefined`, y
   * `undefined.length` deja la pantalla ENTERA en blanco. Ya pasó dos veces
   * en este proyecto: con la ruta de la foto del partido y con el historial.
   * Un carrusel que no se dibuja no se nota; una pantalla en blanco sí.
   */
  const lista = sponsors ?? [];
  if (lista.length === 0) return null;

  function scroll(direction: 1 | -1): void {
    const element = track.current;
    if (element === null) return;
    // Se empuja el 80 % de lo que se ve, no una cantidad fija de píxeles: en
    // un teléfono eso es un logo y medio, y en una pantalla ancha son cuatro.
    element.scrollBy({ left: direction * element.clientWidth * 0.8, behavior: 'smooth' });
  }

  return (
    <section className="sponsors" aria-labelledby="sponsors-title">
      <h2 id="sponsors-title">{t('communities.sponsors')}</h2>

      <div className="sponsor-carousel">
        <button
          type="button"
          className="carousel-arrow"
          aria-label={t('carousel.previous')}
          onClick={() => scroll(-1)}
        >
          <span aria-hidden="true">‹</span>
        </button>

        <ul className="sponsor-track" ref={track} tabIndex={0}>
          {/*
            EL NOMBRE DEBAJO DEL LOGO, escrito.

            Un logo no siempre dice quién es: la mitad son un símbolo sin
            palabras, y a 64 px de alto los que sí las tienen no se leen. El
            nombre debajo es lo que hace que patrocinar sirva —que la gente
            sepa a quién agradecerle el torneo— y además es lo único que se ve
            cuando la imagen no carga.

            EL `alt` DEL LOGO QUEDA VACÍO A PROPÓSITO. Con el nombre escrito
            al lado, un `alt` con el mismo texto se lo hace leer DOS VECES a
            quien usa lector de pantalla. La imagen pasa a ser decorativa
            porque el dato ya está en la página, en texto.
          */}
          {lista.map((sponsor) => (
            <li key={sponsor.id}>
              {sponsor.linkUrl === null ? (
                <>
                  <img src={assetUrl(sponsor.imagePath) ?? ''} alt="" />
                  <span className="sponsor-name">{sponsor.name}</span>
                </>
              ) : (
                // `noopener` no es opcional: sin él, la página que se abre
                // puede manipular la nuestra con `window.opener`.
                <a href={sponsor.linkUrl} target="_blank" rel="noopener noreferrer">
                  <img src={assetUrl(sponsor.imagePath) ?? ''} alt="" />
                  <span className="sponsor-name">{sponsor.name}</span>
                </a>
              )}
            </li>
          ))}
        </ul>

        <button
          type="button"
          className="carousel-arrow"
          aria-label={t('carousel.next')}
          onClick={() => scroll(1)}
        >
          <span aria-hidden="true">›</span>
        </button>
      </div>
    </section>
  );
}
